import React, { useState, useEffect } from 'react';
import './About.css';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaHandshake, FaChartLine, FaLightbulb, FaSearch, FaPencilRuler, FaCode, FaRocket } from 'react-icons/fa';
import { BsCheckCircleFill } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { safeFetch } from '../utils/apiUtils';
import useSEO, { SITE_URL } from '../utils/useSEO';

const ABOUT_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "@id": `${SITE_URL}/about#webpage`,
    "url": `${SITE_URL}/about`,
    "name": "About Nimai Solutions | Web & Mobile App Development Agency Vadodara",
    "description": "Learn about Nimai Solutions — a boutique digital agency in Vadodara, Gujarat delivering premium web development, mobile apps, Flutter and UI/UX design.",
    "isPartOf": { "@id": `${SITE_URL}/#website` },
    "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home",  "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": "About", "item": `${SITE_URL}/about` }
        ]
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: "easeOut" }
    }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

const getPhilosophyIcon = (index) => {
    const icons = [FaShieldAlt, FaHandshake, FaChartLine, FaLightbulb];
    return icons[index % icons.length];
};

const About = () => {
    useSEO({
        title:       "About Nimai Solutions | Web & Mobile App Development Agency Vadodara",
        description: "Learn about Nimai Solutions — a boutique digital agency in Vadodara, Gujarat delivering premium web development, mobile apps, Flutter and UI/UX design services.",
        canonical:   `${SITE_URL}/about`,
        keywords:    "about Nimai Solutions, web development agency Vadodara, digital agency Gujarat, app development team India",
        schema:      ABOUT_SCHEMA,
    });

    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchAboutContent = async () => {
            try {
                const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/about-contents');
                let dataToSet = null;

                if (Array.isArray(result)) {
                    dataToSet = result[0];
                } else if (result.data) {
                    dataToSet = Array.isArray(result.data) ? result.data[0] : result.data;
                }

                if (ok && dataToSet) {
                    setContent(dataToSet);
                }
            } catch (error) {
                console.error('Error fetching about content:', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchAboutContent();
    }, []);

    if (isLoading) {
        return (
            <div className="about-loading">
                <div className="loader"></div>
            </div>
        );
    }

    // Default data if none exists in API
    const displayData = content || {
        hero_title_1: 'Building',
        hero_title_2: 'Technology',
        hero_title_3: 'With',
        hero_title_4: 'Purpose & Integrity',
        hero_description: 'Ethical and purpose-driven digital products designed for human impact. We transform complex data into intuitive solutions that matter.',
        who_we_are_title: 'Professional expertise with human-centric values.',
        who_we_are_description: 'Nimai Solutions is a boutique technology consulting firm that bridges the gap between high-level engineering and ethical responsibility...',
        who_we_are_stats: [
            { value: '150+', label: 'IMPACTFUL PROJECTS' },
            { value: '12+', label: 'COUNTRIES REACHED' }
        ],
        philosophy_pillars: [
            { title: 'Purpose-Driven', description: 'Focusing on impact first. We only build what truly adds value to the end user and society.' },
            { title: 'Ethical', description: 'Transparency and integrity in our code, our pricing, and our client interactions.' },
            { title: 'Scalable', description: 'Building future-proof architectures that grow effortlessly alongside your organization.' },
            { title: 'Partnership', description: 'A collaborative approach to co-creation. We don\'t just work for you; we work with you.' }
        ],
        mission_statement: 'To engineer high-performance digital products through an ethical lens, ensuring technology serves as a force for positive change in the world.',
        vision_statement: 'A digital landscape where every interaction is secure, every design is intentional, and every product is built with long-term human impact in mind.'
    };

    return (
        <div className="about-container">
            <Navbar />

            {/* Hero Section */}
            <section className="about-hero">
                <motion.div
                    className="about-hero-content"
                    initial="hidden" animate="visible" variants={staggerContainer}
                >
                    <motion.h1 className="about-hero-title" variants={fadeInUp}>
                        {displayData.hero_title_1} <span className="about-hero-highlight">{displayData.hero_title_2}</span><br />
                        {displayData.hero_title_3} <span className="about-hero-highlight">{displayData.hero_title_4}</span>
                    </motion.h1>
                    <motion.p className="about-hero-desc" variants={fadeInUp}>
                        {displayData.hero_description}
                    </motion.p>
                    <motion.div
                        className="about-hero-tags"
                        variants={fadeInUp}
                        style={{ display: 'flex', gap: '15px', marginTop: '30px', flexWrap: 'wrap' }}
                    >
                        {['Ethical Innovation', 'Sustainable Tech', 'Human-Centric'].map((tag, i) => (
                            <span key={i} style={{
                                background: 'rgba(255,255,255,0.1)',
                                padding: '10px 20px',
                                borderRadius: '50px',
                                fontSize: '0.9rem',
                                border: '1px solid rgba(255,255,255,0.2)',
                                color: '#e2e8f0',
                                backdropFilter: 'blur(5px)',
                                letterSpacing: '0.5px'
                            }}>
                                • {tag}
                            </span>
                        ))}
                    </motion.div>
                </motion.div>
                <div className="about-hero-shape"></div>
            </section>

            {/* Who We Are */}
            <section className="who-we-are">
                <motion.div
                    initial={{ opacity: 0, x: -50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <span className="section-label">WHO WE ARE</span>
                    <h2 className="who-title">{displayData.who_we_are_title}</h2>
                    <p className="who-desc">{displayData.who_we_are_description}</p>
                    <div className="who-stats">
                        {displayData.who_we_are_stats.map((stat, idx) => (
                            <div className="stat-block" key={idx}>
                                <h4>{stat.value}</h4>
                                <p>{stat.label}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                >
                    <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80" alt="Team working" className="who-image" loading="lazy" />
                </motion.div>
            </section>

            {/* Our Philosophy */}
            <section className="our-philosophy" id="philosophy">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <h2 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '10px' }}>Our Philosophy</h2>
                    <p style={{ color: '#64748b' }}>The core pillars that guide our decision-making and project execution.</p>
                </motion.div>

                <div className="philosophy-grid">
                    {displayData.philosophy_pillars.map((item, index) => {
                        const Icon = getPhilosophyIcon(index);
                        return (
                            <motion.div
                                className="philosophy-card"
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <div className="philosophy-icon"><Icon /></div>
                                <h4>{item.title}</h4>
                                <p>{item.description}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="mission-vision">
                <motion.div
                    className="mv-card"
                    whileHover={{ y: -5 }}
                >
                    <span className="mv-label">OUR MISSION</span>
                    <p className="mv-text">"{displayData.mission_statement}"</p>
                </motion.div>
                <motion.div
                    className="mv-card"
                    whileHover={{ y: -5 }}
                >
                    <span className="mv-label">OUR VISION</span>
                    <p className="mv-text">"{displayData.vision_statement}"</p>
                </motion.div>
            </section>

            {/* How We Work */}
            <section className="how-we-work">
                <h2 style={{ fontSize: '2.5rem', fontWeight: '800' }}>How We Work</h2>
                <p style={{ color: '#64748b', marginBottom: '60px' }}>A streamlined 4-step process designed for clarity and excellence.</p>

                <div className="process-steps">
                    {[
                        { icon: FaSearch, title: "01. Discover", desc: "In-depth research and alignment on goals and ethical impact." },
                        { icon: FaPencilRuler, title: "02. Design", desc: "Human-centric UI/UX that prioritizes accessibility and trust." },
                        { icon: FaCode, title: "03. Develop", desc: "Clean, scalable engineering using modern technology stacks." },
                        { icon: FaRocket, title: "04. Deliver", desc: "Seamless deployment with ongoing support and optimization." }
                    ].map((step, i) => (
                        <motion.div
                            key={i}
                            className="step-item"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.2 }}
                        >
                            <div className="step-icon"><step.icon /></div>
                            <h4 className="step-title">{step.title}</h4>
                            <p className="step-desc">{step.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Ethical Standard */}
            <section className="ethical-standard">
                <div className="standard-content">
                    <h2 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '30px' }}>What Makes Us Different?</h2>
                    <div className="standard-list">
                        {[
                            { title: "No Dark Patterns", desc: "We refuse to use manipulation to improve business metrics." },
                            { title: "Privacy First", desc: "Data retention and security are baked into our architecture from day one." },
                            { title: "Open Communication", desc: "Direct access to lead engineers throughout the entire project lifecycle." }
                        ].map((item, i) => (
                            <div className="standard-item" key={i}>
                                <BsCheckCircleFill className="standard-check" />
                                <div>
                                    <h4 style={{ fontWeight: '700', marginBottom: '5px' }}>{item.title}</h4>
                                    <p style={{ fontSize: '0.9rem', color: '#64748b' }}>{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="standard-card">
                    <h3>Ethical Technology Standard</h3>
                    <p>
                        We follow a strict "Impact-Review" process for every new client. If a project compromises our core ethical values, we simply don't take it. This ensures our team is always passionate about what they build.
                    </p>
                    <div
                        onClick={() => document.getElementById('philosophy')?.scrollIntoView({ behavior: 'smooth' })}
                        style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1rem', fontWeight: '700', cursor: 'pointer', color: 'var(--accent-orange)' }}
                    >
                        Read our Ethics Manifesto <span>➔</span>
                    </div>
                </div>
            </section>

            {/* About CTA */}
            <motion.div
                className="about-cta"
                initial={{ scale: 0.95, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
            >
                <h2>Ready to build something meaningful?</h2>
                <p>Join dozens of organizations that have transformed their digital presence with integrity and purpose.</p>
                <button
                    onClick={() => navigate('/contact')}
                    style={{ background: '#FF9F1C', color: 'white', padding: '15px 40px', fontSize: '1.1rem', borderRadius: '8px', border: 'none', fontWeight: '700', cursor: 'pointer' }}
                >
                    Let's Start a Project
                </button>
            </motion.div>

            <Footer />
        </div>
    );
};

export default About;
